/**
 * tokens.tsx — 设计系统活文档「全局样式」分区
 *
 * 移植自原型 design/Novel-admin/src/pages/design-system-tokens.jsx
 * （色彩 / 布局与密度 / 字体 / 图标 / 明暗模式 / 阴影圆角）。
 * 原型用 window.__dsTokenSections 注册；这里改为具名 ESM export，
 * 由 DesignSystemPage 直接 import 组装。window.Icons → import { Icons }。
 * 图标库展示页用 Icons 的 Object.keys 遍历，复制按钮交互保真。
 */

import { useState } from 'react';
import { Icons } from '~/lib/icons';
import { DSBlock, DSDemo, DSHeader, DSRule, DSToken } from './ds-primitives';

// ============================================================
// 色彩
// ============================================================
export function DSColors() {
  const ACCENT_HUES = [
    { h: 50, name: 'Warm Amber', default: true },
    { h: 25, name: 'Coral' },
    { h: 152, name: 'Forest' },
    { h: 245, name: 'Indigo' },
    { h: 320, name: 'Magenta' },
  ];
  const [hue, setHue] = useState(50);
  return (
    <>
      <DSHeader
        title="色彩"
        sub="单一 accent + 四级状态色 + 五级中性灰。所有颜色基于 oklch 色空间，跨明暗主题保持感知亮度一致。"
      />

      <DSBlock title="Accent · 主题色" sub="全站唯一强调色，可切换 hue">
        <div className="hstack" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {ACCENT_HUES.map((a) => (
            <button
              type="button"
              key={a.h}
              onClick={() => {
                setHue(a.h);
                document.documentElement.style.setProperty('--accent-h', String(a.h));
              }}
              style={{
                padding: '6px 10px',
                border: hue === a.h ? '2px solid var(--accent)' : '1px solid var(--border)',
                borderRadius: 6,
                background: hue === a.h ? 'var(--accent-soft)' : 'var(--bg-elev)',
                cursor: 'pointer',
                font: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: `oklch(0.68 0.17 ${a.h})`,
                }}
              />
              <span style={{ fontSize: 11.5 }}>{a.name}</span>
              {a.default && (
                <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-faint)' }}>
                  DEFAULT
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
          {[
            { v: 'var(--accent)', label: 'accent', mono: 'oklch(.68 .17 h)' },
            { v: 'var(--accent-hover)', label: 'accent-hover', mono: 'oklch(.62 .17 h)' },
            { v: 'var(--accent-soft)', label: 'accent-soft', mono: 'oklch(.95 .04 h)' },
            { v: 'var(--accent-soft-2)', label: 'accent-soft-2', mono: 'oklch(.92 .06 h)' },
            { v: 'var(--accent-fg)', label: 'accent-fg', mono: 'oklch(.20 .04 h)' },
          ].map((c) => (
            <div key={c.label}>
              <div
                style={{
                  height: 56,
                  borderRadius: 6,
                  background: c.v,
                  border: '1px solid var(--border)',
                }}
              />
              <div style={{ marginTop: 4, fontSize: 11, fontWeight: 500 }}>{c.label}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                {c.mono}
              </div>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSBlock title="状态色" sub="只用这四种，不引入新色">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {[
            {
              name: 'pos',
              label: '正向',
              v: 'var(--pos)',
              soft: 'var(--pos-soft)',
              use: '成功 / 在线 / 上架 / 增长',
            },
            {
              name: 'warn',
              label: '警告',
              v: 'var(--warn)',
              soft: 'var(--warn-soft)',
              use: '待处理 / 隐藏 / 限流',
            },
            {
              name: 'neg',
              label: '失败',
              v: 'var(--neg)',
              soft: 'var(--neg-soft)',
              use: '错误 / 下架 / 删除',
            },
            {
              name: 'info',
              label: '信息',
              v: 'var(--info)',
              soft: 'var(--info-soft)',
              use: '提示 / 链接 / 中性强调',
            },
          ].map((s) => (
            <div key={s.name} className="card-flat" style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, height: 56, background: s.v }} />
                <div style={{ flex: 1, height: 56, background: s.soft }} />
              </div>
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 500 }}>{s.label}</div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                  --{s.name} / --{s.name}-soft
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                  {s.use}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSBlock title="中性 · 文字与背景" sub="五级文字 + 五级背景，构建视觉层级">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="label" style={{ marginBottom: 8 }}>
              文字色
            </div>
            <div className="card-flat">
              {[
                { v: '--fg-strong', note: '重要标题 / 数字' },
                { v: '--fg', note: '正文' },
                { v: '--fg-muted', note: '副文 / 说明' },
                { v: '--fg-subtle', note: '占位 / 次要' },
                { v: '--fg-faint', note: '禁用 / 极弱' },
              ].map((t) => (
                <DSToken
                  key={t.v}
                  label={<span style={{ color: `var(${t.v})` }}>{t.v} · 北京 Aurora 12,345</span>}
                  value={t.note}
                  swatch={null}
                  mono={false}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 8 }}>
              背景色
            </div>
            <div className="card-flat">
              {[
                { v: '--bg', note: '页面底色' },
                { v: '--bg-elev', note: '卡片 / 浮层' },
                { v: '--bg-subtle', note: '表头 / 间隔' },
                { v: '--bg-muted', note: '禁用 / 关闭' },
                { v: '--bg-inset', note: '深陷区域 / 代码块' },
              ].map((t) => (
                <DSToken
                  key={t.v}
                  label={t.v}
                  value={t.note}
                  swatch={
                    <span
                      style={{
                        width: 32,
                        height: 22,
                        borderRadius: 4,
                        background: `var(${t.v})`,
                        border: '1px solid var(--border)',
                      }}
                    />
                  }
                  mono={false}
                />
              ))}
            </div>
          </div>
        </div>
      </DSBlock>

      <DSBlock title="图表色板" sub="6 色循环，顺序固定">
        <div className="hstack" style={{ gap: 6 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ flex: 1 }}>
              <div style={{ height: 40, borderRadius: 4, background: `var(--chart-${i})` }} />
              <div
                className="mono"
                style={{ fontSize: 10, marginTop: 4, color: 'var(--fg-muted)' }}
              >
                --chart-{i}
              </div>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSRule tone="do">
        所有颜色通过 CSS variable 引用：<code>color: var(--fg-muted)</code>
      </DSRule>
      <DSRule tone="dont">不要直接写 hex / rgb / oklch 字面量在组件里</DSRule>
      <DSRule tone="note">需要新颜色时优先复用 chart 色板的某一档，再考虑新增 token</DSRule>
    </>
  );
}

// ============================================================
// 布局 & 密度
// ============================================================
export function DSLayout() {
  const [density, setDensity] = useState('compact');
  return (
    <>
      <DSHeader
        title="布局与密度"
        sub="三档密度切换（紧凑 / 标准 / 舒适）+ 统一的 page / card / formrow 骨架"
      />

      <DSBlock title="密度档位">
        <div className="seg" style={{ marginBottom: 14 }}>
          {[
            { id: 'compact', label: '紧凑' },
            { id: 'standard', label: '标准' },
            { id: 'comfortable', label: '舒适' },
          ].map((d) => (
            <button
              type="button"
              key={d.id}
              className={density === d.id ? 'on' : ''}
              onClick={() => {
                setDensity(d.id);
                document.documentElement.setAttribute('data-density', d.id);
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div className="card-flat" style={{ padding: 14 }}>
          <button type="button" className="btn">
            按钮样例
          </button>
          <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--fg-muted)' }}>
            按钮高度、输入框高度、表格行高、padding 都会随密度切换
          </span>
        </div>
      </DSBlock>

      <DSBlock title="间距 Scale">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="card-flat">
            {[
              { name: '--row-h', desc: '表格行高 / 列表项高' },
              { name: '--btn-h', desc: '按钮 / 输入框高度' },
              { name: '--input-h', desc: '输入框 (同 btn-h)' },
              { name: '--pad-x', desc: '卡片水平 padding' },
              { name: '--pad-y', desc: '卡片垂直 padding' },
              { name: '--gap', desc: 'flex / grid 默认 gap' },
            ].map((t) => (
              <DSToken
                key={t.name}
                label={t.name}
                value={t.desc}
                mono={false}
                swatch={
                  <span
                    className="mono"
                    style={{ fontSize: 10, color: 'var(--fg-muted)', width: 40 }}
                  >
                    {getComputedStyle(document.documentElement).getPropertyValue(t.name).trim()}
                  </span>
                }
              />
            ))}
          </div>
          <div className="card-flat" style={{ padding: 14 }}>
            <div className="label" style={{ marginBottom: 10 }}>
              常用 Tailwind 风格手动间距
            </div>
            {[2, 4, 6, 8, 10, 12, 16, 20, 24].map((n) => (
              <div key={n} className="hstack" style={{ gap: 10, marginBottom: 4 }}>
                <span
                  className="mono"
                  style={{ fontSize: 10.5, color: 'var(--fg-muted)', width: 28 }}
                >
                  {n}px
                </span>
                <span
                  style={{ height: 8, background: 'var(--accent)', borderRadius: 2, width: n }}
                />
              </div>
            ))}
          </div>
        </div>
      </DSBlock>

      <DSBlock title="页面骨架">
        <DSDemo
          title="标准页面"
          desc={`<div className="page"> + <div className="page-header"> + 内容`}
          code={`<div className="page">
  <div className="page-header">
    <div>
      <h1 className="page-title">书籍管理</h1>
      <div className="page-sub">12,402 在线 · 全部 App</div>
    </div>
    <button className="btn btn-accent">新建书籍</button>
  </div>
  {/* 内容 */}
</div>`}
        >
          <div style={{ padding: 14, border: '1px dashed var(--border)', borderRadius: 6 }}>
            <div className="hstack" style={{ marginBottom: 12 }}>
              <div>
                <h1 className="page-title" style={{ fontSize: 20 }}>
                  书籍管理
                </h1>
                <div className="page-sub">12,402 在线 · 全部 App</div>
              </div>
              <div style={{ flex: 1 }} />
              <button type="button" className="btn btn-accent">
                <Icons.IconPlus size={11} />
                新建书籍
              </button>
            </div>
            <div
              style={{
                padding: 14,
                background: 'var(--bg-subtle)',
                borderRadius: 6,
                fontSize: 11.5,
                color: 'var(--fg-muted)',
              }}
            >
              内容区域
            </div>
          </div>
        </DSDemo>
      </DSBlock>
    </>
  );
}

// ============================================================
// 字体
// ============================================================
export function DSType() {
  return (
    <>
      <DSHeader
        title="字体"
        sub="Geist (西文 + 数字) + Noto Sans SC (中文) + Geist Mono (代码 / 数字)"
      />

      <DSBlock title="字体家族">
        <div className="vstack" style={{ gap: 8 }}>
          {[
            {
              name: 'Geist',
              usage: '全站默认 sans-serif，无衬线，特别适合数据界面',
              stack: 'Geist, Inter, PingFang SC, Noto Sans SC, system-ui',
            },
            {
              name: 'Geist Mono',
              usage: '所有数字、ID、代码、表格 num 列',
              stack: 'Geist Mono, JetBrains Mono, ui-monospace',
            },
            {
              name: 'Noto Sans SC',
              usage: '中文兜底，与 Geist 视觉等高',
              stack: '落在 sans 栈末尾',
            },
          ].map((f) => (
            <div key={f.name} className="card-flat" style={{ padding: 14 }}>
              <div className="hstack" style={{ gap: 10 }}>
                <span
                  style={{
                    fontFamily: f.name.includes('Mono') ? 'var(--font-mono)' : 'var(--font-sans)',
                    fontSize: 28,
                    fontWeight: 600,
                    color: 'var(--fg-strong)',
                    letterSpacing: '-0.02em',
                    flexShrink: 0,
                  }}
                >
                  {f.name.includes('Mono') ? 'Aa 09 ID-78a' : 'Aa 内容运营'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{f.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
                    {f.usage}
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 4 }}
                  >
                    {f.stack}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSBlock title="字号 Scale">
        <div className="card-flat">
          {[
            { name: '--text-2xs', size: 10.5, use: '极小 · chip · 表头标签' },
            { name: '--text-xs', size: 11.5, use: '副文 · hint' },
            { name: '--text-sm', size: 12.5, use: '表格内容 · 按钮文字' },
            { name: '--text-base', size: 13, use: '正文 · 默认' },
            { name: '--text-md', size: 14.5, use: 'section 标题' },
            { name: '--text-lg', size: 16, use: '卡片标题' },
            { name: '--text-xl', size: 19, use: '' },
            { name: '--text-2xl', size: 23, use: '页面标题 page-title' },
            { name: '--text-3xl', size: 28, use: '登录页 / 大数字' },
            { name: '--text-4xl', size: 36, use: '营销 / hero' },
          ].map((t) => (
            <div
              key={t.name}
              className="hstack"
              style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', gap: 14 }}
            >
              <span
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--fg-muted)', width: 100 }}
              >
                {t.name}
              </span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', width: 40 }}>
                {t.size}px
              </span>
              <span
                style={{
                  fontSize: t.size,
                  fontWeight: t.size >= 19 ? 600 : 400,
                  color: 'var(--fg-strong)',
                  flex: 1,
                  letterSpacing: t.size >= 19 ? '-0.02em' : 0,
                }}
              >
                The quick brown fox · 内容运营 1234
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>{t.use}</span>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSBlock title="字重">
        <div className="hstack" style={{ gap: 20 }}>
          {[400, 500, 600, 700].map((w) => (
            <div key={w}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: w,
                  color: 'var(--fg-strong)',
                  letterSpacing: '-0.02em',
                }}
              >
                Aa 内容
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                weight: {w}
              </div>
            </div>
          ))}
        </div>
      </DSBlock>

      <DSRule tone="do">
        数字 / ID / 邮箱 / hash 一律加 <code className="mono">.mono</code> 类
      </DSRule>
      <DSRule tone="dont">不要用粗体堆叠层级 — 优先调整字号或文字色</DSRule>
      <DSRule tone="note">
        中英混排时 <code>letter-spacing</code> 保持 0；只在大字号 (≥19px) 时收紧到 -0.02em
      </DSRule>
    </>
  );
}

// ============================================================
// 图标
// ============================================================
export function DSIcons() {
  const ICONS = Object.keys(Icons).sort();
  const [q, setQ] = useState('');
  const filtered = ICONS.filter((n) => !q || n.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <DSHeader
        title="图标"
        sub={`${ICONS.length} 枚自绘 SVG 图标 · 全部基于 1.6px stroke + 24×24 viewBox · 单一线条风格`}
      />

      <DSBlock
        title="图标库"
        action={
          <input
            className="input"
            placeholder="搜索图标名…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 200, height: 26 }}
          />
        }
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 4,
          }}
        >
          {filtered.map((name) => {
            const Icon = Icons[name as keyof typeof Icons];
            return (
              <button
                type="button"
                key={name}
                title={`点击复制: <Icons.${name}/>`}
                onClick={() => navigator.clipboard?.writeText(`<Icons.${name}/>`)}
                className="focusable"
                style={{
                  padding: '10px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  background: 'var(--bg-elev)',
                  transition: 'background .12s',
                  font: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-subtle)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-elev)';
                }}
              >
                <Icon size={18} style={{ color: 'var(--fg)' }} />
                <span
                  className="mono"
                  style={{ fontSize: 9.5, color: 'var(--fg-muted)', textAlign: 'center' }}
                >
                  {name.replace(/^Icon/, '')}
                </span>
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            无匹配图标
          </div>
        )}
      </DSBlock>

      <DSBlock title="尺寸 & 颜色">
        <DSDemo
          title="尺寸 size={11..18}"
          desc="size 直接传 number；颜色用 style.color 继承 currentColor"
          code={`<Icons.IconCheck size={14} style={{ color: "var(--pos)" }}/>`}
        >
          <div className="hstack" style={{ gap: 16 }}>
            {[11, 12, 13, 14, 16, 18, 20, 24].map((sz) => (
              <div key={sz} style={{ textAlign: 'center' }}>
                <Icons.IconCheck size={sz} style={{ color: 'var(--accent)' }} />
                <div
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}
                >
                  {sz}
                </div>
              </div>
            ))}
          </div>
        </DSDemo>
      </DSBlock>

      <DSRule tone="do">图标点击行为应有 button 包裹；不要在 svg 上直接绑 onClick</DSRule>
      <DSRule tone="note">
        表格 / 按钮内的图标默认 size 11–13；section 标题用 14–16；空状态用 22+
      </DSRule>
    </>
  );
}

// ============================================================
// 明暗主题
// ============================================================
export function DSTheme() {
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute('data-theme') || 'light',
  );
  const flip = (v: string) => {
    setTheme(v);
    document.documentElement.setAttribute('data-theme', v);
  };
  return (
    <>
      <DSHeader
        title="明暗模式"
        sub="Light / Dark 主题通过 data-theme attribute 切换，所有颜色 token 自动响应"
      />
      <DSBlock
        title="主题切换"
        action={
          <div className="seg">
            <button
              type="button"
              className={theme === 'light' ? 'on' : ''}
              onClick={() => flip('light')}
            >
              <Icons.IconSun size={11} />
              Light
            </button>
            <button
              type="button"
              className={theme === 'dark' ? 'on' : ''}
              onClick={() => flip('dark')}
            >
              <Icons.IconMoon size={11} />
              Dark
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { label: '页面 / 卡片', v: ['--bg', '--bg-elev', '--bg-subtle'] },
            { label: '文字层级', v: ['--fg-strong', '--fg', '--fg-muted', '--fg-faint'] },
          ].map((g) => (
            <div key={g.label} className="card-flat" style={{ padding: 14 }}>
              <div className="label" style={{ marginBottom: 8 }}>
                {g.label}
              </div>
              {g.v.map((v) => (
                <div key={v} className="hstack" style={{ marginBottom: 4 }}>
                  <span
                    style={{
                      width: 28,
                      height: 18,
                      borderRadius: 3,
                      background: v.startsWith('--bg') ? `var(${v})` : 'transparent',
                      border: '1px solid var(--border)',
                    }}
                  />
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: v.startsWith('--bg') ? 'var(--fg-muted)' : `var(${v})`,
                    }}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </DSBlock>
      <DSRule tone="do">所有颜色用 token，不写字面量 — 切换主题时无需任何额外代码</DSRule>
      <DSRule tone="note">
        需要 dark 下额外的色调（更亮的 accent-fg、更暗的 shadow）已在 tokens.css 自动重写
      </DSRule>
    </>
  );
}

// ============================================================
// 阴影 · 圆角
// ============================================================
export function DSShadow() {
  return (
    <>
      <DSHeader title="阴影 & 圆角" sub="四档阴影 + 五档圆角，原则是「越浮越大」" />
      <DSBlock title="阴影">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { name: '--shadow-xs', desc: '卡片 hover / 状态分割' },
            { name: '--shadow-sm', desc: '默认卡片' },
            { name: '--shadow-md', desc: '下拉 / popover' },
            { name: '--shadow-lg', desc: 'Dialog / Drawer' },
          ].map((s) => (
            <div key={s.name} className="card" style={{ padding: 14, boxShadow: `var(${s.name})` }}>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                {s.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </DSBlock>
      <DSBlock title="圆角">
        <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
          {[
            { name: '--radius-sm', v: 4, use: 'chip / kbd' },
            { name: '--radius', v: 6, use: '默认 (button / input)' },
            { name: '--radius-md', v: 8, use: 'card / 输入大尺寸' },
            { name: '--radius-lg', v: 10, use: 'dialog' },
            { name: '--radius-xl', v: 14, use: '营销卡 / hero' },
          ].map((r) => (
            <div key={r.name} style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 80,
                  height: 60,
                  background: 'var(--bg-elev)',
                  border: '1px solid var(--border)',
                  borderRadius: `var(${r.name})`,
                }}
              />
              <div
                className="mono"
                style={{ fontSize: 10, color: 'var(--fg-muted)', marginTop: 4 }}
              >
                {r.name}
              </div>
              <div style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}>{r.use}</div>
            </div>
          ))}
        </div>
      </DSBlock>
    </>
  );
}
